import _ from 'lodash'
import axios from 'axios'
import snakeCaseKeys from 'snakecase-keys'
import camelcaseKeys from 'camelcase-keys'
import kebabcaseKeys from 'kebabcase-keys'
import { notification } from 'antd'
import i18next from 'i18next'

import Storage from '@/libs/services/storage'
import { API_BASE_URL } from '@/config/app'

// Utility function to handle navigation to login
const navigateToLogin = () => {
  // Clear any existing tokens
  Storage.signOut()
  // Use window.location for navigation since we're in a service file
  window.location.href = '/auth/login'
}

const api = axios.create({
  baseURL: `${API_BASE_URL}/api/`,
  timeout: 60 * 1000,
})

const errorFn = err => err
const successFn = resp => {
  const userToken = _.get(resp, 'headers.authorization', _.get(resp, 'data.access', null))
  const refreshToken = _.get(resp, 'data.refresh', null)
  if (userToken) {
    Storage.updateUserToken(userToken)
  }
  if (refreshToken) {
    Storage.updateRefreshToken(refreshToken)
  }
  // Convert snake_case keys to camelCase keys recursively.
  resp.data = camelcaseKeys(resp.data, { deep: true })
  return resp
}
const promise = ({ sError, sSuccess }) => {
  if (axios.isCancel()) {
    return new Promise((resolve, reject) => reject(errorFn(sError)))
  }
  return new Promise((resolve, reject) =>
    sError
      ? reject(errorFn(sError))
      : sSuccess.data.error
        ? reject(errorFn(sSuccess))
        : resolve(successFn(sSuccess))
  )
}

const buildBody = ({ body, files }) => {
  const snakecaseBody = !_.isEmpty(body) ? snakeCaseKeys(body) : {}
  // Get rid of null files
  const nonNullFiles = _.pickBy(files, _.identity)
  if (!_.isEmpty(nonNullFiles)) {
    const formData = new FormData()
    Object.keys(nonNullFiles).forEach(filename =>
      formData.append(_.snakeCase(filename), nonNullFiles[filename])
    )
    Object.keys(snakecaseBody).forEach(key => formData.append(key, snakecaseBody[key]))
    return { data: formData, headers: { 'Content-Type': 'multipart/form-data' } }
  }

  return !_.isEmpty(snakecaseBody)
    ? { data: snakecaseBody, headers: { 'Content-Type': 'application/json' } }
    : {}
}

class AxiosRequest {
  constructor(host) {
    this.host = host
  }

  _methodConfig = {
    PATCH: { requiresBody: true, acceptsFiles: false },
    POST: { requiresBody: true, acceptsFiles: true },
    PUT: { requiresBody: true, acceptsFiles: true },
    GET: { requiresBody: false, acceptsFiles: false },
    DELETE: { requiresBody: true, acceptsFiles: false },
  }

  _createMethod =
    method =>
    (params = {}) => {
      const { requiresBody, acceptsFiles } = this._methodConfig[method]
      const baseParams = {
        method,
        service: params.service,
        query: params.query,
        uri: params.uri,
        host: this.host,
        version: params.version,
        skipAuth: params.skipAuth,
        skipSnakeCase: params.skipSnakeCase ?? false,
      }

      if (requiresBody) {
        baseParams.body = params.body
      }

      if (acceptsFiles) {
        baseParams.files = params.files
      }

      return this._sendRequest(baseParams)
    }

  // HTTP method implementations
  patch = this._createMethod('PATCH')

  post = this._createMethod('POST')

  put = this._createMethod('PUT')

  get = this._createMethod('GET')

  delete = this._createMethod('DELETE')

  _sendRequest = request => {
    if (!request) {
      return promise({ sError: 'request empty' })
    }
    const { body, files, query, host, method, service, uri, version, skipAuth, skipSnakeCase } =
      request
    if (!method || !version) {
      return promise({ sError: 'method or version is empty' })
    }
    let url = [host, version, service, uri].filter(v => v).join('/')
    if (query) {
      // _.pickBy: get rid of null/undefined fields
      const processedQuery = _.pickBy(query, _.identity)
      const queryParams = skipSnakeCase ? processedQuery : kebabcaseKeys(processedQuery)
      const queryString = Object.keys(queryParams)
        .map(key => `${key}=${queryParams[key]}`)
        .join('&')
      url = `${url}?${queryString}`
    }
    let tokenRefreshed = false
    const executeCall = () => {
      const { CancelToken } = axios
      const source = CancelToken.source()
      const bodyConfig = skipSnakeCase
        ? { data: body, files } // Send body as-is when skipSnakeCase is true
        : buildBody({ body, files }) // Use existing buildBody for snake_case conversion

      const req = _.merge(
        {
          method,
          url,
          cancelToken: source.token,
        },
        bodyConfig,
        skipAuth === true ? {} : { headers: { Authorization: `Bearer ${Storage.getUserToken()}` } }
      )
      this.cancelTokenSource = source

      const handleError = err => {
        if (
          err.response &&
          err.response.status === 403 &&
          !tokenRefreshed &&
          !skipAuth &&
          !_.isEmpty(Storage.getRefreshToken())
        ) {
          tokenRefreshed = true
          return this.post({
            body: { refresh: Storage.getRefreshToken() },
            service: 'token',
            uri: 'refresh/',
            version,
          })
            .then(() => executeCall())
            .catch(refreshErr => {
              notification.error({
                message: i18next.t('app.msg.sessionExpired'),
                placement: 'topRight',
                duration: 4.5,
              })
              // Clear localStorage when refresh token fails
              Storage.cleanUp()
              // Navigate to login page
              navigateToLogin()

              return promise({ sError: refreshErr })
            })
        }
        return promise({ sError: err })
      }
      return api(req)
        .then(resp => promise({ sSuccess: resp }))
        .catch(handleError)
    }

    return executeCall()
  }
}

// eslint-disable-next-line react-refresh/only-export-components
export const configureLangHeader = (lang = 'es') => {
  api.defaults.headers.common['Accept-Language'] = lang
}

export default AxiosRequest
